import React, { useState } from "react";
import Layout from "../Layout/Layout";
import { useAuth } from "../../hooks/useAuth";
import { formatCPF, removeCPFMask, isValidCPFLength } from "../../utils/cpfUtils";
import { useSearchParams } from "react-router-dom";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    nome: "",
    cpf: "",
    estrangeiro: false,
    email: "",
    confirmarEmail: "",
    password: "",
    password_confirmation: "",
    tipo_usuario: "proponente" as "admin" | "proponente",
    telefone: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    return localStorage.getItem("register_error") || "";
  });

  const { register } = useAuth();



  // Limpa localStorage quando usuário muda de página (não durante re-mounts)
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("register_error");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    let finalValue = type === "checkbox" ? checked : value;
    
    // Aplica máscara no CPF (apenas se não for estrangeiro)
    if (name === "cpf" && !formData.estrangeiro) {
      finalValue = formatCPF(value as string);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.nome.trim()) return "Nome é obrigatório";
    if (!formData.email.trim()) return "Email é obrigatório";
    if (formData.email !== formData.confirmarEmail)
      return "Emails não coincidem";
    if (!formData.password.trim()) return "Senha é obrigatória";
    if (formData.password.length < 6)
      return "Senha deve ter pelo menos 6 caracteres";
    if (formData.password !== formData.password_confirmation)
      return "Senhas não coincidem";

    if (!formData.estrangeiro) {
      if (!formData.cpf.trim()) return "CPF é obrigatório";
      if (!isValidCPFLength(formData.cpf))
        return "CPF deve ter exatamente 11 dígitos";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      localStorage.removeItem("register_error");
      setError("");
      const registerData = {
        nome: formData.nome.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        tipo_usuario: formData.tipo_usuario,
        estrangeiro: formData.estrangeiro,
        ...(formData.cpf && { cpf: removeCPFMask(formData.cpf) }),
        ...(formData.telefone && { telefone: formData.telefone.trim() }),
      };

      await register(registerData);

      sessionStorage.setItem("registration_success", "true");

      setTimeout(() => {
        const returnTo = searchParams.get("return_to");
        const email = encodeURIComponent(formData.email.trim().toLowerCase());
        window.location.href = returnTo
          ? `/login?email=${email}&return_to=${encodeURIComponent(returnTo)}`
          : "/register/success";
      }, 100);
    } catch (err) {
      let errorMessage = "Erro ao fazer cadastro";

      if (err instanceof Error) {
        errorMessage = err.message;
        if (errorMessage.includes("Cpf has already been taken")) {
          errorMessage = "Este CPF já possui cadastro no sistema";
        } else if (errorMessage.includes("Email has already been taken")) {
          errorMessage = "Este email já possui cadastro no sistema";
        }
      }

      localStorage.setItem("register_error", errorMessage);
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Cadastro</h1>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Tab Header */}
            <div className="bg-indigo-900 text-white px-6 py-4">
              <h2 className="text-lg font-semibold">Informações Pessoais</h2>
            </div>

            {/* Form Content */}
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6">
                DADOS PESSOAIS
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Nome e CPF */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome Completo <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-1 flex flex-col md:flex-row md:items-end gap-4">
                      <div className="flex items-center gap-4 pb-0 md:pb-2 order-1 md:order-2">
                        <span className="text-sm font-medium text-gray-700">Estrangeiro?</span>
                        <label className="flex items-center gap-1">
                          <input
                            type="radio"
                            name="estrangeiro"
                            checked={formData.estrangeiro === true}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                estrangeiro: true,
                              }))
                            }
                            className="w-4 h-4 text-indigo-900"
                          />
                          <span className="text-sm">SIM</span>
                        </label>
                        <label className="flex items-center gap-1">
                          <input
                            type="radio"
                            name="estrangeiro"
                            checked={formData.estrangeiro === false}
                            onChange={() =>
                              setFormData((prev) => ({
                                ...prev,
                                estrangeiro: false,
                              }))
                            }
                            className="w-4 h-4 text-indigo-900"
                          />
                          <span className="text-sm">NÃO</span>
                        </label>
                      </div>

                      <div className="flex-1 order-2 md:order-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.estrangeiro ? "Passaporte" : "CPF"} <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          name="cpf"
                          value={formData.cpf}
                          onChange={handleChange}
                          placeholder={
                            formData.estrangeiro
                              ? "Número do Passaporte"
                              : "123.456.789-01"
                          }
                          maxLength={formData.estrangeiro ? 20 : 14}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar E-mail <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        name="confirmarEmail"
                        value={formData.confirmarEmail}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Senha */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Senha <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar senha <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="password"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-900 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Mensagens de erro e sucesso */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                      {error}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-indigo-900 text-white px-8 py-3 rounded-md font-semibold hover:bg-indigo-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Cadastrando..." : "Cadastrar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
